import { useCallback } from 'react'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import HighlightLabel from 'LuxKit/Label/HighlightLabel'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import LinePenIcon from 'LuxKit/Icons/line/LinePenIcon'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import Image from 'components/Common/Image'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import CartPanelAccommodationTileLayout from './CartPanelAccommodationTileLayout'
import useCartPanelHotelOfferData from '../hooks/useCartPanelHotelOfferData'
import type { CartPanelAccommodationItem } from '../hooks/useCartPanelItems'

const RoomCard = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.three};
  border-radius: ${props => props.theme.borderRadius.M};
  padding: ${rem(10)} ${rem(12)};
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  align-self: flex-start;
  width: fit-content;
  min-width: min(${rem(300)}, 100%);
  max-width: 100%;
  container-type: inline-size;
  container-name: room-card;
`

const RoomCardRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  flex-wrap: wrap;
`

const ChangeRoomButtonWrapper = styled.div`
  @container room-card (max-width: ${rem(360)}) {
    flex-basis: 100%;
    align-self: flex-start;
  }
`

const RoomImageWrapper = styled.div`
  width: ${rem(80)};
  height: ${rem(80)};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
  flex-shrink: 0;
`

const RoomCardContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
`

interface Props {
  item: CartPanelAccommodationItem
  onToggleInclude: (item: CartPanelAccommodationItem) => void
  onSetDates: () => void
  onChangeRoom: (item: CartPanelAccommodationItem) => void
  onDelete: (item: CartPanelAccommodationItem) => void
  isStreaming: boolean
}

function CartPanelNeedsDatesTile({
  item,
  onToggleInclude,
  onSetDates,
  onChangeRoom,
  onDelete,
  isStreaming,
}: Props) {
  const intl = useIntl()
  const { pkg } = useCartPanelHotelOfferData(item)

  const handleChangeRoom = useCallback(() => {
    onChangeRoom(item)
  }, [onChangeRoom, item])

  const handleDelete = useCallback(() => {
    onDelete(item)
  }, [onDelete, item])

  const roomImage = pkg?.roomType.images[0]

  const header = (
    <Heading variant="heading4">{item.name}</Heading>
  )

  const roomCard = (item.roomName || item.occupancy) ? (
    <RoomCard>
      {item.packageAutoSelected && (
        <HighlightLabel variant="primaryLight" startIcon={<SolidSparklesIcon />}>
          {intl.formatMessage({
            id: 'KUUE4XT7BqIq4IZ5Hmc0',
            defaultMessage: 'Picked for you',
            description: 'Label indicating the room was auto-selected by the system in the cart panel',
          })}
        </HighlightLabel>
      )}
      <RoomCardRow>
        {roomImage && (
          <RoomImageWrapper>
            <Image image={roomImage} width={80} height={80} fit="center" objectFit="cover" dpr={2} />
          </RoomImageWrapper>
        )}
        <RoomCardContent>
          {item.roomName && <BodyText variant="medium" weight="bold">{item.roomName}</BodyText>}
          {item.occupancy && <BodyText variant="medium" colour="neutral-three">{item.occupancy}</BodyText>}
        </RoomCardContent>
        <ChangeRoomButtonWrapper>
          <TextButton kind="tertiary" size="small" startIcon={<LinePenIcon />} onClick={handleChangeRoom} disabled={isStreaming}>
            {intl.formatMessage({
              id: 'BrfTYdJonj0A3hETLQR8',
              defaultMessage: 'Change room',
              description: 'Link to change the selected room for a hotel in the cart panel',
            })}
          </TextButton>
        </ChangeRoomButtonWrapper>
      </RoomCardRow>
    </RoomCard>
  ) : undefined

  const attention = (
    <Group direction="vertical" gap={8}>
      <MessagingLabel variant="warningLight">
        {intl.formatMessage({
          id: 'hqRyBjYFB4btbjAKSi2i',
          defaultMessage: 'Set dates to checkout',
          description: 'Attention indicator prompting the user to set trip dates for a hotel in the cart panel',
        })}
      </MessagingLabel>
      <TextButton kind="tertiary" size="small" onClick={onSetDates} disabled={isStreaming}>
        {intl.formatMessage({
          id: 'BicBNDg-DjRAqHnDPAGo',
          defaultMessage: 'Set dates',
          description: 'Action button to set trip dates from the cart panel',
        })}
      </TextButton>
    </Group>
  )

  const deleteAction = (
    <TextButton kind="secondary" size="small" shape="round" startIcon={<LineTrashAltIcon />} onClick={handleDelete} disabled={isStreaming}>
      {intl.formatMessage({
        id: 'jDEZjhV3eo-cnxIBHOtR',
        defaultMessage: 'Delete',
        description: 'Button to permanently delete an item from the trip planner cart panel',
      })}
    </TextButton>
  )

  const price = item.pricing.price ? (
    <PriceRowPrice
      price={item.pricing.price}
      priceFormat="preciseDollar"
      saleUnit=""
      size="S"
    />
  ) : undefined

  return (
    <CartPanelAccommodationTileLayout
      item={item}
      onToggleInclude={onToggleInclude}
      header={header}
      roomCard={roomCard}
      attention={attention}
      deleteAction={deleteAction}
      price={price}
    />
  )
}

export default CartPanelNeedsDatesTile
